import { useMemo, useState } from "react";
import {
  Users,
  Mountain,
  Tent,
  PartyPopper,
  Snowflake,
  Car,
  Wallet,
  Weight,
  Sofa,
  BadgeEuro,
  CloudRain,
  ArrowLeft,
  RotateCcw,
  Sparkles,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ZeltCard } from "@/components/ZeltCard";
import { cn } from "@/lib/utils";
import type { FinderAnswers, FinderBudget, FinderEinsatz, FinderPrioritaet, FinderResult, Zelt } from "@/data/zelte";

interface ZeltFinderProps {
  onNavigate: (path: string) => void;
  onToggleVergleich: (zelt: Zelt) => void;
  vergleichIds: string[];
  initialAnswers?: Partial<FinderAnswers>;
  results: FinderResult[];
  loading?: boolean;
  onSubmit: (answers: FinderAnswers) => void;
  onReset: () => void;
}

const personenOptions = [
  { value: 1, label: "1 Person", hint: "Solo & Thru-Hike" },
  { value: 2, label: "2 Personen", hint: "Paar oder Freunde" },
  { value: 4, label: "3–4 Personen", hint: "Kleine Gruppe" },
  { value: 6, label: "5+ Personen", hint: "Familie oder Crew" },
];

const einsatzOptions: { value: FinderEinsatz; label: string; hint: string; icon: typeof Tent }[] = [
  { value: "camping", label: "Campingplatz", hint: "Komfort, Vorraum, Stehhöhe", icon: Tent },
  { value: "trekking", label: "Trekking", hint: "Leicht, kleines Packmaß", icon: Mountain },
  { value: "festival", label: "Festival", hint: "Schnell aufgebaut, günstig", icon: PartyPopper },
  { value: "winter", label: "Winter / Allwetter", hint: "4-Saison, windstabil", icon: Snowflake },
  { value: "auto", label: "Autodach", hint: "Dachzelt fürs Fahrzeug", icon: Car },
];

const budgetOptions: { value: FinderBudget; label: string; hint: string }[] = [
  { value: "unter-150", label: "Bis 150 €", hint: "Einstieg & Festival" },
  { value: "150-400", label: "150 – 400 €", hint: "Solide Allrounder" },
  { value: "400-plus", label: "Ab 400 €", hint: "Premium & Expedition" },
];

const prioOptions: { value: FinderPrioritaet; label: string; hint: string; icon: typeof Weight }[] = [
  { value: "gewicht", label: "Gewicht", hint: "Jedes Gramm zählt", icon: Weight },
  { value: "komfort", label: "Komfort", hint: "Platz und Stehhöhe", icon: Sofa },
  { value: "preis", label: "Preis", hint: "Beste Ersparnis", icon: BadgeEuro },
  { value: "wetter", label: "Wetterfestigkeit", hint: "Regen, Wind, Schnee", icon: CloudRain },
];

function OptionCard({
  selected,
  onClick,
  children,
  className,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "text-left rounded-2xl border-2 p-4 transition-all duration-200",
        "hover:-translate-y-0.5 hover:shadow-md",
        selected
          ? "border-primary bg-primary/10 shadow-sm shadow-primary/20"
          : "border-transparent bg-muted/40 hover:border-border",
        className
      )}
    >
      {children}
    </button>
  );
}

export function ZeltFinder({
  onNavigate,
  onToggleVergleich,
  vergleichIds,
  initialAnswers,
  results,
  loading = false,
  onSubmit,
  onReset,
}: ZeltFinderProps) {
  const [step, setStep] = useState(initialAnswers && hasCompleteAnswers(initialAnswers) ? 5 : 1);
  const [answers, setAnswers] = useState<Partial<FinderAnswers>>(initialAnswers || {});

  const progress = Math.min(step, 4) / 4;

  const canContinue = useMemo(() => {
    if (step === 1) return typeof answers.personen === "number";
    if (step === 2) return Boolean(answers.einsatz);
    if (step === 3) return Boolean(answers.budget);
    if (step === 4) return Boolean(answers.prioritaet);
    return false;
  }, [answers, step]);

  const goNext = () => {
    if (step < 4) {
      setStep(step + 1);
      return;
    }
    if (hasCompleteAnswers(answers)) {
      onSubmit(answers);
      setStep(5);
    }
  };

  const reset = () => {
    setAnswers({});
    setStep(1);
    onReset();
  };

  return (
    <div>
      {step <= 4 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-muted-foreground">Schritt {step} von 4</p>
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </button>
            )}
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
      )}

      {step === 1 && (
        <section>
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Für wie viele Personen?</h2>
          <p className="text-muted-foreground mb-6">Lieber etwas Luft einplanen als zu eng schlafen.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {personenOptions.map(option => (
              <OptionCard
                key={option.value}
                selected={answers.personen === option.value}
                onClick={() => setAnswers(prev => ({ ...prev, personen: option.value }))}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">{option.label}</div>
                    <div className="text-sm text-muted-foreground">{option.hint}</div>
                  </div>
                </div>
              </OptionCard>
            ))}
          </div>
        </section>
      )}

      {step === 2 && (
        <section>
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Wofür brauchen Sie das Zelt?</h2>
          <p className="text-muted-foreground mb-6">Der Einsatz entscheidet über Gewicht, Aufbau und Wetterschutz.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {einsatzOptions.map(option => (
              <OptionCard
                key={option.value}
                selected={answers.einsatz === option.value}
                onClick={() => setAnswers(prev => ({ ...prev, einsatz: option.value }))}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <option.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">{option.label}</div>
                    <div className="text-sm text-muted-foreground">{option.hint}</div>
                  </div>
                </div>
              </OptionCard>
            ))}
          </div>
        </section>
      )}

      {step === 3 && (
        <section>
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Welches Budget?</h2>
          <p className="text-muted-foreground mb-6">Wir zeigen Modelle in Ihrer Preisklasse zuerst.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {budgetOptions.map(option => (
              <OptionCard
                key={option.value}
                selected={answers.budget === option.value}
                onClick={() => setAnswers(prev => ({ ...prev, budget: option.value }))}
              >
                <Wallet className="h-5 w-5 text-primary mb-3" />
                <div className="font-semibold">{option.label}</div>
                <div className="text-sm text-muted-foreground">{option.hint}</div>
              </OptionCard>
            ))}
          </div>
        </section>
      )}

      {step === 4 && (
        <section>
          <h2 className="text-2xl md:text-3xl font-bold mb-2">Was zählt am meisten?</h2>
          <p className="text-muted-foreground mb-6">Damit sortieren wir die Empfehlungen.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {prioOptions.map(option => (
              <OptionCard
                key={option.value}
                selected={answers.prioritaet === option.value}
                onClick={() => setAnswers(prev => ({ ...prev, prioritaet: option.value }))}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <option.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">{option.label}</div>
                    <div className="text-sm text-muted-foreground">{option.hint}</div>
                  </div>
                </div>
              </OptionCard>
            ))}
          </div>
        </section>
      )}

      {step === 5 && (
        <section aria-live="polite">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-medium text-primary mb-2">
                <Sparkles className="h-4 w-4" />
                Ihre Empfehlungen
              </div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Diese Zelte passen am besten</h2>
              <p className="text-muted-foreground">
                Sortiert nach Ihren Angaben zu Personen, Einsatz, Budget und Priorität.
              </p>
            </div>
            <Button variant="outline" onClick={reset} className="rounded-xl gap-2 self-start">
              <RotateCcw className="h-4 w-4" />
              Neu starten
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="rounded-2xl border bg-card overflow-hidden">
                  <div className="aspect-[4/3] bg-muted shimmer" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-muted rounded-full w-1/3 shimmer" />
                    <div className="h-6 bg-muted rounded-full w-3/4 shimmer" />
                  </div>
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-16 rounded-2xl border bg-muted/20">
              <h3 className="text-xl font-semibold mb-2">Keine Treffer für diese Kombination</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Versuchen Sie ein anderes Budget oder einen anderen Einsatzzweck.
              </p>
              <Button onClick={reset} className="rounded-xl">Angaben ändern</Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {results.map((result, index) => (
                <div key={result.zelt.id} className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="inline-flex items-center justify-center h-6 min-w-6 px-2 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                      {index + 1}
                    </span>
                    <span className="font-medium text-muted-foreground">Empfehlung</span>
                  </div>
                  <ZeltCard
                    zelt={result.zelt}
                    onNavigate={onNavigate}
                    onToggleVergleich={onToggleVergleich}
                    isInVergleich={vergleichIds.includes(result.zelt.id)}
                  />
                  {result.gruende.length > 0 && (
                    <ul className="space-y-1.5 px-1">
                      {result.gruende.map(grund => (
                        <li key={grund} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>{grund}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {step <= 4 && (
        <div className="mt-8 flex justify-end">
          <Button
            size="lg"
            disabled={!canContinue}
            onClick={goNext}
            className="h-12 px-6 rounded-xl"
          >
            {step === 4 ? "Empfehlungen zeigen" : "Weiter"}
          </Button>
        </div>
      )}
    </div>
  );
}

function hasCompleteAnswers(answers: Partial<FinderAnswers>): answers is FinderAnswers {
  return (
    typeof answers.personen === "number" &&
    Boolean(answers.einsatz) &&
    Boolean(answers.budget) &&
    Boolean(answers.prioritaet)
  );
}
